import { useState, useEffect, useCallback } from "react";
import type { CalendarEvent, CalendarsResponse } from "@/types/calendar";
import { CALENDAR_CONFIG } from "@/config/rooms";

export function useCalendarData() {
  const [data, setData] = useState<Map<string, CalendarEvent[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchCalendars = useCallback(async () => {
    try {
      const response = await fetch("/api/calendars", { cache: "no-store" });
      if (!response.ok) throw new Error("Failed to fetch calendars");

      const result: CalendarsResponse = await response.json();

      const newData = new Map<string, CalendarEvent[]>();
      for (const cal of result.calendars) {
        // Convert date strings back to Date objects
        const events = cal.events.map((e: any) => ({
          ...e,
          start: new Date(e.start),
          end: new Date(e.end),
        }));
        newData.set(cal.roomId, events);
      }

      setData(newData);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Optimistically add an event to a room without waiting for Google propagation.
  // Google's resource calendar sync can lag 3-30s; this keeps the UI responsive.
  const insertEventLocal = useCallback((roomId: string, event: CalendarEvent) => {
    setData((prev) => {
      const next = new Map(prev);
      const existing = next.get(roomId) || [];
      if (existing.some((e) => e.id === event.id)) return prev;
      const merged = [...existing, event].sort(
        (a, b) => a.start.getTime() - b.start.getTime(),
      );
      next.set(roomId, merged);
      return next;
    });
  }, []);

  const moveEventLocal = useCallback(
    (
      fromRoomId: string,
      toRoomId: string,
      eventId: string,
      newStart: Date,
      newEnd: Date,
    ) => {
      setData((prev) => {
        const fromList = prev.get(fromRoomId) || [];
        const source = fromList.find((e) => e.id === eventId);
        if (!source) {
          console.log(`move_local_skip reason=source_not_found eventId=${eventId} fromRoomId=${fromRoomId}`);
          return prev;
        }
        const next = new Map(prev);
        next.set(fromRoomId, fromList.filter((e) => e.id !== eventId));
        const dedup = (next.get(toRoomId) || []).filter((e) => e.id !== eventId);
        const moved: CalendarEvent = { ...source, start: newStart, end: newEnd };
        const merged = [...dedup, moved].sort(
          (a, b) => a.start.getTime() - b.start.getTime(),
        );
        next.set(toRoomId, merged);
        console.log(`move_local_ok eventId=${eventId} from=${fromRoomId} to=${toRoomId} start=${newStart.toISOString()}`);
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    fetchCalendars();

    const interval = setInterval(
      fetchCalendars,
      CALENDAR_CONFIG.refreshInterval
    );
    return () => clearInterval(interval);
  }, [fetchCalendars]);

  return {
    data,
    loading,
    error,
    lastRefresh,
    refetch: fetchCalendars,
    insertEventLocal,
    moveEventLocal,
  };
}
